// EP-160 T160-104l (S6, AC-11): the tenant's own PDPA data-subject requests.
//
// Mirrors `api/app/controllers/api/customer_app/data_subject_requests_controller.rb`:
//   GET  /api/customer/data_subject_requests             -> { data: DataSubjectRequest[] }
//   POST /api/customer/data_subject_requests             -> { data: DataSubjectRequest }
//   GET  /api/customer/data_subject_requests/:id/export  -> { data: { url, expires_in } }
// The export answers 409 export_not_ready, 410 export_expired, 404 otherwise.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { isNativePlatform } from '@/lib/native'

export const DATA_SUBJECT_REQUEST_TYPES = [
  'access',
  'erasure',
  'rectification',
  'restriction',
  'objection',
  'withdrawal',
] as const

export type DataSubjectRequestType = (typeof DATA_SUBJECT_REQUEST_TYPES)[number]

export type DataSubjectRequestStatus = 'pending' | 'processing' | 'completed' | 'rejected'

// The tenant has to say what they want for these three (the server answers
// 422 note_required otherwise).
export const NOTE_REQUIRED_TYPES: readonly DataSubjectRequestType[] = [
  'rectification',
  'restriction',
  'objection',
]

export interface DataSubjectRequest {
  id: number
  request_type: DataSubjectRequestType
  status: DataSubjectRequestStatus
  note: string | null
  requested_at: string
  due_at: string | null
  processed_at: string | null
  export_ready: boolean
  export_expires_at: string | null
  // EP-162 item 9.2: the owner or an admin lifted the restriction this
  // request set (null otherwise, and absent from an older server).
  lifted_at?: string | null
}

export interface CreateDataSubjectRequestInput {
  request_type: DataSubjectRequestType
  note?: string
}

export const dataSubjectRequestKeys = {
  all: ['customer', 'data_subject_requests'] as const,
}

export function useDataSubjectRequests() {
  return useQuery({
    queryKey: dataSubjectRequestKeys.all,
    queryFn: async () => {
      const response = await api.get<{ data: DataSubjectRequest[] }>(
        '/api/customer/data_subject_requests',
      )
      return response.data.data
    },
  })
}

export function useCreateDataSubjectRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateDataSubjectRequestInput) => {
      const response = await api.post<{ data: DataSubjectRequest }>(
        '/api/customer/data_subject_requests',
        input,
      )
      return response.data.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dataSubjectRequestKeys.all })
    },
  })
}

/** Mints the 15-minute download URL for a completed access request. */
export async function fetchDataSubjectExportUrl(id: number): Promise<string> {
  const response = await api.get<{ data: { url: string; expires_in: number } }>(
    `/api/customer/data_subject_requests/${id}/export`,
  )
  return response.data.data.url
}

/**
 * Opens the presigned export URL in the system browser where the app has one,
 * so the tenant's download never lands inside the app's own web view.
 *
 * - Native shell (EP-162 item 9.7): window.open(url, '_blank'). Capacitor
 *   hands a new-window request to the system browser (iOS:
 *   WebViewDelegationHandler createWebViewWith; Android: the bridge allows
 *   JavaScript windows), the way a target="_blank" link reaches Safari
 *   (EP-126). To check on a real phone at the next tenant app build: the URL
 *   is only known after an await, after the tap.
 * - Inside LINE: liff.openWindow with external: true, the system browser too
 *   (LINE's in-app browser blocks a window.open that follows an await).
 * - Any other browser: a top-level navigation; the URL answers with
 *   Content-Disposition: attachment, so the page stays where it is.
 */
export async function openDataSubjectExport(url: string): Promise<void> {
  if (isNativePlatform()) {
    window.open(url, '_blank')
    return
  }
  try {
    const liff = (await import('@line/liff')).default
    if (liff.isInClient()) {
      liff.openWindow({ url, external: true })
      return
    }
  } catch {
    // SDK not ready: fall through to a plain navigation.
  }
  window.location.assign(url)
}
